// Reports performance visualizations — bespoke SVG charts that pixel-match the
// design ReportsScreen (ScoreRings / GoalRing / ScoreLanes + the overall/chart
// glass cards). Prefix: rp-. Ported verbatim from the design's rp-* CSS.
export const perfChartsStyles = `
  /* PERF layout — overall card + chart card */
  .rp-perf {
    display: grid;
    grid-template-columns: minmax(260px, 320px) 1fr;
    gap: var(--space-5);
    align-items: stretch;
    margin-bottom: var(--space-5);
  }
  /* Solid frost (no live backdrop-filter): these are large cards on the
     scrolling reports page — a blur(24px) here re-rasterizes every scroll
     frame → jank. Uses --glass-flat-solid per its own guidance in index.css. */
  .rp-overall, .rp-chartwrap {
    border-radius: var(--rad-catchup-large);
    border: 1px solid var(--glass-flat-border);
    background: var(--glass-flat-solid);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
    padding: var(--space-5);
  }
  .rp-overall { display: flex; flex-direction: column; gap: var(--space-4); }
  .rp-overall__title { font-size: var(--fs-2xl); font-weight: 700; color: var(--text-primary); }
  .rp-chartwrap { display: flex; }

  /* SCORE RINGS — concentric radial gauges */
  .rp-rings { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-5); padding: var(--space-4) 0; }
  .rp-rings__chart { position: relative; width: 184px; height: 184px; }
  .rp-rings__center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; }
  .rp-rings__num { font-size: 40px; font-weight: 700; color: var(--text-primary); line-height: 1; }
  .rp-rings__lbl { font-size: var(--fs-sm); font-weight: 700; letter-spacing: 0.2px; color: var(--text-muted); text-transform: uppercase; max-width: 64px; text-align: center; line-height: 1.1; }
  .rp-rings__legend { width: 100%; display: flex; flex-direction: column; gap: var(--space-2); }
  .rp-rings__row { display: flex; align-items: center; gap: var(--space-3); padding: 7px 10px; border-radius: var(--rad-catchup-medium); background: rgba(255, 255, 255, 0.45); }
  .rp-rings__dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
  .rp-rings__name { flex: 1; min-width: 0; font-size: var(--fs-md); font-weight: 600; color: var(--text-secondary); }
  .rp-rings__val { font-size: var(--fs-xl); font-weight: 700; }
  .rp-rings__empty { display: flex; flex-direction: column; align-items: center; gap: var(--space-3); color: var(--text-muted); }
  .rp-rings__emptydot { width: 88px; height: 88px; border-radius: 50%; border: 8px solid var(--catchup-gray-100); display: flex; align-items: center; justify-content: center; font-size: 28px; color: var(--catchup-gray-300); }
  .rp-rings__empty span { font-size: var(--fs-md); font-weight: 600; }

  /* GOAL RING — captured vs goal gauge (single score) */
  .rp-goal { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-4); padding: var(--space-2) 0; }
  .rp-goal__cap { font-size: var(--fs-lg); font-weight: 700; color: var(--text-secondary); }
  .rp-goal__main { display: flex; align-items: center; gap: var(--space-2); }
  .rp-goal__chart { position: relative; width: 200px; height: 200px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .rp-goal__svg { position: absolute; inset: 0; filter: drop-shadow(0 4px 10px rgba(15, 72, 89, 0.10)); }
  .rp-goal__disc { position: relative; width: 112px; height: 112px; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.74));
    -webkit-backdrop-filter: blur(14px) saturate(1.5); backdrop-filter: blur(14px) saturate(1.5);
    box-shadow: inset 0 2px 5px rgba(255, 255, 255, 0.95), inset 0 -7px 16px rgba(15, 72, 89, 0.10), 0 6px 16px rgba(15, 72, 89, 0.12); }
  .rp-goal__num { font-size: 30px; font-weight: 700; color: var(--text-primary); line-height: 1; letter-spacing: -0.5px; }
  .rp-goal__sub { font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.6px; }
  @media (prefers-reduced-motion: reduce) { .rp-goal__svg circle { transition: none !important; } }

  /* legend (shared) */
  .rp-legend { display: flex; gap: var(--space-5); justify-content: center; flex-wrap: wrap; }
  .rp-legend__item { display: inline-flex; align-items: center; gap: 8px; font-size: var(--fs-md); color: var(--text-secondary); font-weight: 600; }
  .rp-legend__item i { width: 13px; height: 13px; border-radius: 50%; flex-shrink: 0; }
  .rp-chart__title { font-size: var(--fs-2xl); font-weight: 700; color: var(--text-primary); }

  /* SCORE LANES — one rail per assignment (student bar + branch/class markers) */
  .rp-lanes { flex: 1; display: flex; flex-direction: column; gap: var(--space-4); }
  .rp-lanes__head { display: flex; flex-direction: column; gap: var(--space-3); }
  .rp-lg { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
  .rp-lg--you { width: 16px; height: 16px; box-shadow: 0 0 0 3px rgba(249, 128, 102, 0.25); }
  .rp-legtog { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; font-family: var(--font-sans);
    font-size: var(--fs-md); font-weight: 600; color: var(--text-secondary); padding: 5px 12px; border-radius: var(--rad-catchup-full);
    border: 1px solid var(--glass-flat-border); background: var(--glass-flat-solid); -webkit-backdrop-filter: none; backdrop-filter: none;
    box-shadow: var(--glass-flat-edge); transition: all var(--duration-base) var(--ease-standard); }
  .rp-legtog:hover { border-color: var(--brand); }
  .rp-legtog.is-on { background: var(--glass-active-bg); border-color: var(--glass-active-border); color: var(--brand-dark); }
  .rp-legtog__hint { font-size: var(--fs-sm); font-weight: 700; opacity: 0.6; }
  .rp-legtog.is-on .rp-legtog__hint { opacity: 1; color: var(--brand-dark); }
  .rp-lanes__scale { display: flex; justify-content: space-between; padding: 0 38px 0 calc(180px + var(--space-4)); }
  .rp-lanes__tick { font-size: var(--fs-sm); font-weight: 700; color: var(--text-muted); transform: translateX(-50%); }
  .rp-lanes__tick:first-child { transform: none; }
  .rp-lanes__tick:last-child { transform: translateX(-100%); }
  .rp-lanes__list { display: flex; flex-direction: column; }
  .rp-lane { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-4) 0; }
  .rp-lane + .rp-lane { border-top: 1px solid var(--glass-flat-border); }
  .rp-lane__name { width: 180px; flex-shrink: 0; font-size: var(--fs-md); font-weight: 700; color: var(--text-primary); line-height: 1.25;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .rp-lane__track { position: relative; flex: 1; min-width: 0; }
  .rp-lane__rail { position: relative; height: 16px; border-radius: 99px; background: var(--catchup-gray-100);
    box-shadow: inset 0 1px 3px rgba(15, 72, 89, 0.12); margin-right: 30px; }
  .rp-lane__fill { position: absolute; left: 0; top: 0; height: 100%; border-radius: 99px; min-width: 0;
    background: linear-gradient(90deg, #FBA08C, var(--accent-coral)); transition: width 0.9s cubic-bezier(0.22, 1, 0.36, 1); }
  .rp-lane__cap { position: absolute; top: 50%; transform: translate(8px, -50%); font-size: var(--fs-md); font-weight: 700; color: var(--accent-coral-text);
    transition: left 0.9s cubic-bezier(0.22, 1, 0.36, 1); white-space: nowrap; }
  .rp-lane__grid { position: absolute; top: 2px; bottom: 2px; width: 1px; background: rgba(255, 255, 255, 0.6); }
  .rp-mark { position: absolute; top: 50%; transform: translate(-50%, -50%); z-index: 3; display: flex; flex-direction: column; align-items: center;
    transition: left 0.9s cubic-bezier(0.22, 1, 0.36, 1); }
  .rp-mark__diamond { width: 14px; height: 14px; border-radius: 3px; transform: rotate(45deg); border: 2px solid #fff; box-shadow: 0 3px 7px -2px rgba(15, 72, 89, 0.45); }
  .rp-mark__lbl { position: absolute; bottom: 100%; margin-bottom: 5px; font-size: var(--fs-sm); font-weight: 700; white-space: nowrap;
    background: var(--glass-popover-bg-strong); -webkit-backdrop-filter: none; backdrop-filter: none;
    padding: 1px 7px; border-radius: 99px; box-shadow: var(--glass-flat-edge), 0 2px 6px -2px rgba(15, 72, 89, 0.3); }
  .rp-mark--alt .rp-mark__lbl { bottom: auto; top: 100%; margin-bottom: 0; margin-top: 5px; }
  .rp-lane__empty { font-size: var(--fs-sm); font-weight: 600; color: var(--text-muted); }

  /* PERF TABLES — design rp-list grid */
  /* Solid frost (no live backdrop-filter): the list card spans the scrolling
     reports page; a blur(24px) here re-blurs every scroll frame → jank.
     Uses --glass-flat-solid per its own guidance in index.css. */
  .rp-list {
    border-radius: var(--rad-catchup-large);
    border: 1px solid var(--glass-flat-border);
    background: var(--glass-flat-solid);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
    overflow: hidden;
  }
  .rp-list__row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr 1.3fr 0.7fr 1.5fr;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-6);
    border-bottom: 1px solid var(--glass-flat-border);
  }
  .rp-list__row--simple { grid-template-columns: 2fr 1fr 1.2fr 1.3fr 0.7fr 1.5fr; }
  .rp-list__row--nofb { grid-template-columns: 2fr 1fr 1.2fr 1.3fr 0.7fr; }
  .rp-list__row--exam { grid-template-columns: 2fr 1.3fr 1.3fr 1.4fr; }
  /* An exam list whose rows open a detail page (the deneme karne): one more, narrow column for the eye. */
  .rp-list__row--exam-detail { grid-template-columns: 2fr 1.3fr 1.3fr 1.4fr 0.6fr; }
  /* Sınavlar with Deneme selected: one more column for the Tahmini puan chips (never folded into %). */
  .rp-list__row--exam-score { grid-template-columns: 2fr 1.1fr 1.1fr 1.3fr 1.6fr 0.6fr; }
  .rp-list__row:last-child { border-bottom: none; }
  .rp-list__row--head { background: var(--glass-active-bg); }
  .rp-list__row--head .rp-lc {
    font-size: var(--fs-sm);
    font-weight: 700;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    text-transform: uppercase;
  }
  .rp-lc {
    display: flex;
    flex-direction: column;
    font-size: var(--fs-base);
    color: var(--text-secondary);
    min-width: 0;
  }
  .rp-lc b { font-weight: 700; color: var(--text-primary); }
  .rp-lc small { font-size: var(--fs-sm); color: var(--text-muted); }
  .rp-lc--name {
    flex-direction: row;
    align-items: center;
    gap: var(--space-2);
    font-weight: 700;
    color: var(--text-primary);
  }
  .rp-lc--name .rp-name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Name over a muted second line (a deneme's Tahmini puan). */
  .rp-lc--name .rp-name-stack {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .rp-lc--c { align-items: center; }
  /* Deneme sınavları "Puan" cell: one chip per Tahmini puan (an AYT has up to four); a partial one is
     muted amber and says "kısmi". A deneme without a score keeps its Başarı bar with a muted note. */
  .rp-score-chips { display: flex; flex-wrap: wrap; gap: var(--space-1) var(--space-2); }
  .rp-score-chip {
    display: inline-flex;
    align-items: baseline;
    gap: var(--space-1);
    padding: 2px var(--space-2);
    border-radius: var(--radius-full);
    background: var(--brand-50);
    border: 1px solid var(--glass-active-border);
    color: var(--brand-dark);
    white-space: nowrap;
  }
  .rp-score-chip__type { font-size: var(--fs-xs); font-weight: 700; }
  .rp-score-chip__value { font-size: var(--fs-md); font-weight: 800; color: var(--text-primary); font-variant-numeric: tabular-nums; }
  .rp-score-chip__partial { font-size: var(--fs-xs); font-weight: 600; color: var(--color-warning-text); }
  .rp-score-chip--partial { background: var(--color-warning-bg); border-color: var(--color-warning-border); }
  .rp-score-fallback { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .rp-score-none { font-size: var(--fs-xs); color: var(--text-muted); }
  /* A destek row with no number to show: results not published yet, or nothing graded yet. Words,
     never a 0% bar — a 0 would read as a fail the learner never had. */
  .rp-score-state { font-size: var(--fs-sm); font-weight: 600; color: var(--text-muted); }
  /* Destek before → after under its bar; a drop reads in the coral the bar already uses for < 50. */
  .rp-score-delta { font-size: var(--fs-xs); font-weight: 600; color: var(--color-success-text); }
  .rp-score-delta.is-down { color: var(--accent-coral-text); }
  .rp-dash { color: var(--text-muted); }
  .rp-idx {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--brand-100);
    color: var(--brand-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--fs-sm);
    font-weight: 700;
    flex-shrink: 0;
  }
  .rp-iico { display: inline-flex; flex-shrink: 0; }
  .rp-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--fs-md);
    font-weight: 600;
  }
  .rp-status--warn { color: var(--color-warning-text); }
  .rp-status--ok { color: var(--color-success-text); }
  .rp-prog { display: flex; align-items: center; gap: var(--space-3); }
  .rp-prog__bar {
    flex: 1;
    height: 10px;
    border-radius: 99px;
    background: var(--catchup-gray-100);
    overflow: hidden;
  }
  .rp-prog__bar span { display: block; height: 100%; border-radius: 99px; }
  .rp-prog__val {
    font-size: var(--fs-md);
    font-weight: 700;
    color: var(--text-secondary);
    white-space: nowrap;
  }
  .rp-prog__val.is-danger { color: var(--accent-coral-text); }
  .rp-eye {
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 17px;
    display: inline-flex;
    padding: 0;
  }

  /* feedback expand panel (preserves the AI-feedback toggle behavior) */
  .rp-fb-wrap { border-bottom: 1px solid var(--glass-flat-border); }
  .rp-fb-panel {
    transition: all 500ms ease-in-out;
    background: var(--brand-50);
    overflow: hidden;
  }
  .rp-fb-panel--open {
    max-height: 1200px;
    opacity: 1;
    padding: var(--space-4) var(--space-6);
  }
  .rp-fb-panel--closed {
    max-height: 0;
    opacity: 0;
    padding: 0 var(--space-6);
  }

  /* "Feedback" pill is now the shared library SecondaryButton. */

  @media (max-width: 1080px) {
    .rp-perf { grid-template-columns: 1fr; }
  }
  /* Phone width: the 5–7 column grid cannot fit, so a row becomes two columns with the name (and its
     kind) across the top; the header row is dropped because its labels no longer sit over their cells. */
  @media (max-width: 767px) {
    .rp-list__row,
    .rp-list__row--simple,
    .rp-list__row--nofb,
    .rp-list__row--exam,
    .rp-list__row--exam-detail,
    .rp-list__row--exam-score {
      grid-template-columns: 1fr 1fr;
      gap: var(--space-2) var(--space-3);
      padding: var(--space-3) var(--space-4);
    }
    .rp-list__row > .rp-lc--name,
    .rp-list__row > .rp-lc--prog { grid-column: 1 / -1; }
    .rp-list__row--head { display: none; }
  }
`;
